// Feedback console: every firm's requests in one queue, waiting-on-us first, with the response-time numbers.
// Requests with firm activity you haven't opened are marked New; the Feedback nav badge counts them. One table
// row per request; the whole row opens it. At phone width the table scrolls sideways inside its box.
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Link2, Lock, Paperclip, Search } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { fmtAgo } from '../../lib/format';
import { useMe } from '../AdminShell';
import { Banner, Button, Card, Pill, Select, Spinner, TableBox, Td, Th, cx } from '../../ui';
import {
  KIND_LABEL, STATUS_LABEL, STATUS_TONE, URGENCY_LABEL, AREA_LABEL, firmLabel, fmtCalendarDate,
  type FeedbackStatus, type QueueRow, type Stats,
} from './types';

function Stat({ label, value, warn }: { label: string; value: string | number; warn?: boolean }) {
  return (
    <Card className="flex items-baseline gap-2 px-3 py-2">
      <span className={cx('text-lg font-bold leading-none tabular-nums', warn ? 'text-warn-fg' : 'text-ink')}>{value}</span>
      <span className="text-xs font-semibold text-ink-2">{label}</span>
    </Card>
  );
}

const hours = (h: number | null) => (h == null ? '—' : h < 24 ? `${h}h` : `${Math.round((h / 24) * 10) / 10}d`);

export function FeedbackQueuePage() {
  const [rows, setRows] = useState<QueueRow[] | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [status, setStatus] = useState('open');
  const [kind, setKind] = useState('');
  const [firm, setFirm] = useState('');
  const [firms, setFirms] = useState<Array<{cell_id: string; firm_key: string; firm_name: string}>>([]);
  const [waiting, setWaiting] = useState(false);
  const [onlyNew, setOnlyNew] = useState(false);
  const { feedbackNew, reportFeedbackSeen } = useMe();
  const newCount = feedbackNew ?? 0;
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (kind) params.set('kind', kind);
    if (firm) { const [cell, key] = JSON.parse(firm) as string[]; params.set('cell', cell!); params.set('firm', key!); }
    if (waiting) params.set('waiting', 'true');
    if (onlyNew) params.set('new', 'true');
    if (q.trim()) params.set('q', q.trim());
    try {
      const [list, s, f] = await Promise.all([
        api.get<{ requests: QueueRow[] }>(`/api/admin/feedback?${params}`),
        api.get<{ stats: Stats }>('/api/admin/feedback/stats'),
        api.get<{ firms: typeof firms }>('/api/admin/feedback/firms'),
      ]);
      setRows(list.requests);
      setStats(s.stats);
      setFirms(f.firms);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load feedback.');
    }
  }, [status, kind, firm, waiting, onlyNew, q]);

  const markAllRead = async () => {
    try { await api.post('/api/admin/feedback/seen'); setOnlyNew(false); await load(); reportFeedbackSeen(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not mark feedback as read.'); }
  };

  useEffect(() => {
    const t = window.setTimeout(() => void load(), q ? 250 : 0);
    return () => window.clearTimeout(t);
  }, [load, q]);

  return (
    <div className="mx-auto flex w-full max-w-shell flex-col gap-4">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <h1 className="page-title">Feedback</h1>
        <p className="m-0 text-[13px] text-muted">Bugs, ideas and questions from every firm. Internal notes and their files stay here; firms never see them.</p>
      </div>

      {newCount > 0 && (
        <Banner tone="info">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-semibold">{newCount} {newCount === 1 ? 'request has' : 'requests have'} something new for you</span>
            <span className="text-ink-2">New requests, replies and reopens from firms since you last opened them.</span>
            <span className="ml-auto flex gap-2">
              {!onlyNew && <Button size="sm" variant="outline" onClick={() => { setOnlyNew(true); setStatus(''); }}>Show them</Button>}
              <Button size="sm" variant="link" onClick={() => void markAllRead()}>Mark all as read</Button>
            </span>
          </div>
        </Banner>
      )}

      {stats && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <Stat label="Open" value={stats.open_count} />
          <Stat label="Median first reply" value={hours(stats.median_first_response_hours)} />
          <Stat label="No reply 2+ days" value={stats.no_reply_2_days} warn={stats.no_reply_2_days > 0} />
          <Stat label="Reopened (30 days)" value={stats.reopened_30_days} warn={stats.reopened_30_days > 0} />
          <Stat label="Median to Done" value={stats.median_days_to_done == null ? '—' : `${stats.median_days_to_done}d`} />
          <Stat label="Need-by at risk" value={stats.need_by_at_risk} warn={stats.need_by_at_risk > 0} />
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <label className="relative flex-1 basis-56">
          <span className="sr-only">Search</span>
          <Search aria-hidden size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="FB-0012 or words"
            className="min-h-11 w-full rounded-[10px] border border-line-btn bg-white pl-9 pr-3 text-sm text-ink"
          />
        </label>
        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} small>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
          <option value="">All</option>
          {(Object.keys(STATUS_LABEL) as FeedbackStatus[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </Select>
        <Select label="Firm" value={firm} onChange={(e) => setFirm(e.target.value)} small>
          <option value="">All firms</option>
          {firms.map((f) => <option key={JSON.stringify([f.cell_id, f.firm_key])} value={JSON.stringify([f.cell_id, f.firm_key])}>{f.firm_name} · {f.cell_id}</option>)}
        </Select>
        <Select label="Kind" value={kind} onChange={(e) => setKind(e.target.value)} small>
          <option value="">All kinds</option>
          <option value="bug">Bugs</option>
          <option value="feature">Features / Ideas</option>
          <option value="question">Questions</option>
        </Select>
        <label className="flex min-h-11 items-center gap-2 text-sm font-medium text-ink">
          <input type="checkbox" checked={waiting} onChange={(e) => setWaiting(e.target.checked)} className="h-4 w-4" />
          Waiting on us
        </label>
        <label className="flex min-h-11 items-center gap-2 text-sm font-medium text-ink">
          <input type="checkbox" checked={onlyNew} onChange={(e) => setOnlyNew(e.target.checked)} className="h-4 w-4" />
          New to me
        </label>
      </div>

      {error && <Banner tone="error" role="alert">{error}</Banner>}
      {!rows ? <Spinner label="Loading feedback" /> : rows.length === 0 ? (
        <Card className="px-6 py-10 text-center text-muted">Nothing here.</Card>
      ) : (
        <QueueTable rows={rows} />
      )}
    </div>
  );
}

const URGENCY_SHORT: Record<string, string> = { blocking: 'Blocking', slowing: 'Slowing', fyi: 'FYI' };
const isOpenStatus = (s: string) => ['received', 'planned', 'in_progress'].includes(s);

/** One request per line: the whole row opens it. New for you: a dot, a bold title and a tinted row. Waiting on us,
 * reopened, open questions and file counts sit after the title. */
function QueueTable({ rows }: { rows: QueueRow[] }) {
  const navigate = useNavigate();
  return (
    <TableBox minWidth={1100} label="Feedback requests" className="[&_table]:table-fixed">
      <thead className="border-b border-line bg-subtle">
        <tr>
          <Th className="w-[92px]">Ref</Th>
          <Th>Request</Th>
          <Th className="w-[150px]">From</Th>
          <Th className="w-[124px]">Kind</Th>
          <Th className="w-[84px]">Urgency</Th>
          <Th className="w-[128px]">Status</Th>
          <Th className="w-[118px]">Due</Th>
          <Th className="w-[86px] text-right">Updated</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const fresh = (r.new_count ?? 0) > 0;
          const late = Boolean(r.need_by && isOpenStatus(r.status) && (!r.estimated_completion || r.estimated_completion > r.need_by));
          const href = `/admin/feedback/${r.id}`;
          return (
            <tr key={r.id} onClick={(e) => { if (!(e.target as HTMLElement).closest('a')) navigate(href); }}
              className={cx('cursor-pointer border-t border-line-soft hover:bg-row-hi', fresh && 'bg-brand-tint')}>
              <Td className="whitespace-nowrap py-2.5 text-[13px] font-bold tabular-nums text-brand">
                {fresh && <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-brand align-middle" title={r.new_count === 1 ? 'Something new for you' : `${r.new_count} new for you`}><span className="sr-only">New: </span></span>}{r.reference}
              </Td>
              <Td className="overflow-hidden py-2.5">
                <div className="flex min-w-0 items-center gap-2">
                  <Link to={href} className={cx('min-w-0 truncate text-ink no-underline hover:underline', fresh ? 'font-bold' : 'font-semibold')} title={r.title}>{r.title}</Link>
                  <span className="flex flex-none items-center gap-1.5 text-xs text-muted">
                    {r.waiting_on_us && <Pill tone="warn" dot className="!px-2 !py-0 !text-xs">Waiting on us</Pill>}
                    {r.reopened_at && <Pill tone="warn" className="!px-2 !py-0 !text-xs">Reopened</Pill>}
                    {r.open_questions > 0 && <span title={`${r.open_questions} open question${r.open_questions === 1 ? '' : 's'}`}>{r.open_questions}?</span>}
                    {r.internal_note_count > 0 && <span className="inline-flex items-center gap-0.5" title={`${r.internal_note_count} internal`}><Lock aria-label="internal notes" size={12} />{r.internal_note_count}</span>}
                    {r.attachment_count > 0 && <span className="inline-flex items-center gap-0.5" title={`${r.attachment_count} files`}><Paperclip aria-label="files" size={12} />{r.attachment_count}</span>}
                    {r.link_count > 0 && <span className="inline-flex items-center gap-0.5" title={`${r.link_count} linked`}><Link2 aria-label="linked requests" size={12} />{r.link_count}</span>}
                  </span>
                </div>
              </Td>
              <Td className="py-2.5 text-[13px]">
                <div className="truncate" title={`${r.submitted_by_name} · ${firmLabel(r)}`}>
                  <span className="font-semibold text-ink-2">{r.submitted_by_name}</span> <span className="text-muted">{firmLabel(r)}</span>
                </div>
              </Td>
              <Td className="py-2.5 text-[13px]">
                <div className="truncate" title={r.area ? `${KIND_LABEL[r.kind]} · ${AREA_LABEL[r.area]}` : KIND_LABEL[r.kind]}>
                  {KIND_LABEL[r.kind]}{r.area && <span className="text-muted"> · {AREA_LABEL[r.area]}</span>}
                </div>
              </Td>
              <Td className={cx('whitespace-nowrap py-2.5 text-[13px]', r.urgency === 'blocking' ? 'font-semibold text-warn-fg' : 'text-muted')}
                title={r.urgency ? URGENCY_LABEL[r.urgency] : undefined}>{r.urgency ? URGENCY_SHORT[r.urgency] ?? URGENCY_LABEL[r.urgency] : '—'}</Td>
              <Td className="whitespace-nowrap py-2 text-[13px]"><Pill tone={STATUS_TONE[r.status]} dot>{STATUS_LABEL[r.status]}</Pill></Td>
              <Td className="whitespace-nowrap py-2.5 text-[13px] text-muted">
                {late
                  ? <span className="font-semibold text-warn-fg" title={r.estimated_completion ? `Est. ${fmtCalendarDate(r.estimated_completion)}` : 'No estimate'}>Need by {fmtCalendarDate(r.need_by)}</span>
                  : r.estimated_completion ? `Est. ${fmtCalendarDate(r.estimated_completion)}`
                  : r.need_by ? `Need by ${fmtCalendarDate(r.need_by)}` : '—'}
              </Td>
              <Td className="whitespace-nowrap py-2.5 text-right text-[13px] text-muted">{fmtAgo(r.updated_at)}</Td>
            </tr>
          );
        })}
      </tbody>
    </TableBox>
  );
}
